import axios, { type AxiosRequestConfig, type AxiosResponse } from "axios";
import type { ApiResponse } from "./ApiResponse";

import { extractErrorMessage, showError } from "../errorservices/errorservices";
import { getToken } from "@/app/services/context/tokenGetter";
import { aiEndPoints } from "../urlConfig/apiEndpoints";
import { executeLogout } from "@/app/utils/loginFlow/logoutHelper";
import { store, type RootState } from "@/app/redux/store";
import {
  CORRELATION_ID_HEADER,
  generateCorrelationId,
} from "@/app/utils/loginFlow/correlationId";

const getTenantId = (): string | undefined => {
  const state = store.getState() as RootState;
  return state.userData?.data?.tenantId;
};

// ---------------- Auth logout registration ----------------
const handleUnauthorized = (err: any) => {
  executeLogout();
  return Promise.reject(err);
};

// ---------------- Generic API handler ----------------
const handleApiRequest = async <T>(
  method: typeof axios.get | typeof axios.post | typeof axios.delete,
  url: string,
  payload: any = {},
): Promise<ApiResponse<T>> => {
  try {
    const correlationId = generateCorrelationId();
    const token = getToken();
    const tenantId = getTenantId();
    const headers: Record<string, string | undefined> = {
      Accept: "*/*",
      Authorization: `Bearer ${token}`,
      appId: tenantId,
      [CORRELATION_ID_HEADER]: correlationId,
    };
    if (!(payload instanceof FormData))
      headers["Content-Type"] = "application/json";

    const config: AxiosRequestConfig = { headers };
    const response: AxiosResponse =
      method === axios.get || method === axios.delete
        ? await method(url, { ...config, params: payload })
        : await method(url, payload, config);

    const data = response.data;

    return data;
  } catch (err: any) {
    const { code, message } = extractErrorMessage(err);

    if (code === 401) {
      return handleUnauthorized(err);
    }

    showError(code, message);
    return Promise.reject(err);
  }
};

// ---------------- AiHealth API handlers ----------------
export const gxAiHealthApi = () =>
  handleApiRequest<any>(axios.get, aiEndPoints.gxAihealth, false);

// ----------------gxAiqueryApi API handlers ----------------

export const gxAiqueryApi = async (payload: any) => {
  const response = await fetch(aiEndPoints.gxAiquery, {
    method: "POST",
    headers: {
      Accept: "text/event-stream",
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.body) {
    throw new Error("No response body (stream not available).");
  }

  return response.body;
};
